export interface PollUntilResult<T> {
  /** True when `isReady` matched before the poll ran out of attempts/time. */
  ready: boolean;
  /** The last value returned by `poll`. */
  value: T;
}

export interface PollUntilOptions<T> {
  /** Performs one poll attempt and returns the latest value. */
  poll: () => Promise<T>;
  /** Returns true once polling should stop successfully. */
  isReady: (value: T) => boolean;
  /** Returns true when polling should stop by throwing `getFailureError`. */
  isFailed?: (value: T) => boolean;
  /** Builds the error thrown when `isFailed` matches. Defaults to a generic message. */
  getFailureError?: (value: T) => Error;
  /** Delay between attempts, in ms. Also the starting delay when `backoffMultiplier` is set. */
  intervalMs: number;
  /** Multiplier applied to the delay after each attempt. Omit for a fixed interval. */
  backoffMultiplier?: number;
  /** Upper bound for the (possibly backed-off) delay. Defaults to `intervalMs`. */
  maxIntervalMs?: number;
  /** Wall-clock budget for the whole poll, in ms. Provide this and/or `maxAttempts`. */
  deadlineMs?: number;
  /** Maximum number of poll attempts. Provide this and/or `deadlineMs`. */
  maxAttempts?: number;
  /** Wait one interval before the first poll attempt instead of polling immediately. */
  delayBeforeFirstPoll?: boolean;
  /** Overrides how the wait between attempts is performed. Defaults to a real `setTimeout`. */
  sleep?: (ms: number) => Promise<void>;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Repeatedly calls `poll` until `isReady` matches, `isFailed` matches (throws), or the
 * `deadlineMs`/`maxAttempts` budget runs out (returns `{ ready: false, value }` — does not throw).
 */
export async function pollUntil<T>(options: PollUntilOptions<T>): Promise<PollUntilResult<T>> {
  const {
    poll,
    isReady,
    isFailed,
    getFailureError,
    intervalMs,
    backoffMultiplier = 1,
    maxIntervalMs = intervalMs,
    deadlineMs,
    maxAttempts,
    delayBeforeFirstPoll = false,
    sleep = defaultSleep,
  } = options;

  if (deadlineMs == null && maxAttempts == null) {
    throw new Error("pollUntil requires deadlineMs and/or maxAttempts");
  }

  const deadline = deadlineMs != null ? Date.now() + deadlineMs : undefined;
  let delay = intervalMs;
  let attempt = 0;

  if (delayBeforeFirstPoll) {
    await sleep(intervalMs);
  }

  for (;;) {
    const value = await poll();

    if (isReady(value)) {
      return { ready: true, value };
    }
    if (isFailed?.(value)) {
      throw getFailureError ? getFailureError(value) : new Error("Polling failed");
    }

    attempt++;
    if (maxAttempts != null && attempt >= maxAttempts) {
      return { ready: false, value };
    }

    if (deadline != null) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        return { ready: false, value };
      }
      await sleep(Math.min(delay, remaining));
    } else {
      await sleep(delay);
    }
    delay = Math.min(Math.round(delay * backoffMultiplier), maxIntervalMs);
  }
}
